/**
 * 应用内导航轨迹（仅内存，不落盘、不跨会话）。
 *
 * 用途：分析页的「返回」按钮要按**来路**给出准确的文案与落点——
 * 从首页来应显示「返回首页」并回首页，从「我的分析」来应显示「返回我的分析」。
 * `history.back()` 的目标无法预知，做不了按钮文案，因此自己记账。
 *
 * 注意：只按 pathname 记账。同一页面内用 replace 改 query（如消费 forceRerun、
 * 解绑旧任务）不会产生新条目，只会更新栈顶的 search。
 */

type NavEntry = { pathname: string; search: string };

const MAX_ENTRIES = 50;
const stack: NavEntry[] = [];

export function recordNavPath(pathname: string, search: string): void {
  const top = stack[stack.length - 1];
  if (top && top.pathname === pathname) {
    top.search = search;
    return;
  }
  stack.push({ pathname, search });
  if (stack.length > MAX_ENTRIES) stack.splice(0, stack.length - MAX_ENTRIES);
}

/** 来路 → 按钮文案。未知来路用中性文案，落点交给 goBack 兜底。 */
export function originLabel(pathname: string): string {
  if (pathname === '/' || pathname.startsWith('/?') || pathname.startsWith('/home')) {
    return '返回首页';
  }
  if (pathname.startsWith('/my-analysis')) return '返回我的分析';
  if (pathname.startsWith('/leads')) return '返回线索池';
  if (pathname.startsWith('/my/')) return '返回工作台';
  return '返回上一步';
}

/**
 * 距当前分析页最近的一条「非分析页」来路。
 *
 * 新标签直接打开分析页时栈里只有自己 → 返回 null，调用方退回 `goBack()` 兜底。
 */
export function previousOrigin(
  currentPathname: string,
): { path: string; label: string } | null {
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    const entry = stack[i];
    if (entry.pathname === currentPathname) continue;
    if (entry.pathname.startsWith('/single-analysis')) continue;
    return {
      path: `${entry.pathname}${entry.search}`,
      label: originLabel(entry.pathname),
    };
  }
  return null;
}

/** 本次文档加载时的首个路由（SPA 客户端导航不会改变它）。 */
const BOOT_LOCATION = `${window.location.pathname}${window.location.search}`;

/**
 * 当前路由是否就是「本次文档加载时的路由」。
 *
 * ⚠️ `isPageReload()` 只描述**文档**的加载方式：只要标签页刷新过一次，
 * 之后的 React Router 客户端导航它也返回 true。因此「刷新保护」必须先用本函数
 * 确认当前路由就是文档加载时的路由，否则从其它页面客户端跳进分析页会被误判为
 * 刷新——表现为「点分析 → 被踢回首页，什么都没发生」。
 */
export function isDocumentBootLocation(pathname: string, search: string): boolean {
  return `${pathname}${search}` === BOOT_LOCATION;
}
